import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';
import { HTTPService } from 'src/app/services/http.service';
import { FormulaCatalogService } from './formula-catalog.service';
import { FuncaoStudio, Variable } from './formula-catalog.types';

interface Categoria { key: string; label: string; icon: string; items: FuncaoStudio[]; aberta: boolean; }
interface Exemplo { label: string; formula: string; variaveis: Variable[]; modo?: 'single' | 'code'; }
interface TraceItem { lineNo: number; formula: string; result: any; error?: string; varSet?: string; ms: number; }

const CATS: { key: string; label: string; icon: string; re: RegExp }[] = [
  { key: 'regex', label: 'Regex', icon: 'filter', re: /^regex/i },
  { key: 'texto', label: 'Texto', icon: 'font-size', re: /(replace|case|trim|Espacos|left|right|pad|extract|substring|split|concat|formatString|onlyAlpha|onlyNumbers|remover|lenSize|sanitize|decodeString|encodeString|html2text|removerLB|Quote|Comment)/i },
  { key: 'json', label: 'JSON', icon: 'code', re: /(json|stringify|arrayToObject|isJson|escapeJson|jsonToCsv|transformJSON|toJSONArray)/i },
  { key: 'xml', label: 'XML', icon: 'code-sandbox', re: /(xml|jdom|JArray|toXMLArray|SelectXml|selectXML|byteToXmlJson)/i },
  { key: 'data', label: 'Data', icon: 'calendar', re: /(date|Date|sumDays|sumHours|parseTimestamp|toTimestamp|truncDate|datePart|getDate|createDate)/i },
  { key: 'math', label: 'Matematica', icon: 'calculator', re: /^(add|subtract|multiply|divide|roundValue)$/i },
  { key: 'sql', label: 'SQL / Banco', icon: 'database', re: /(executeSql|executeProcedure|createJSONArray|inCollection|inUnion|inOracle|getActiveCompanies|getContractPref|getToData)/i },
  { key: 'mongo', label: 'Mongo', icon: 'database', re: /^mongo/i },
  { key: 'cripto', label: 'Cripto / Auth', icon: 'lock', re: /(crypto|decrypt|encrypt|encode|decode|MD5|Authentication|sign|Sign|Sha|SHA|hmac|Hmac|Header|shein|aliexpress|temu|Temu|shopee|Shopee)/i },
  { key: 'logica', label: 'Logica', icon: 'branches', re: /^(ifThenElse|dataSwitch|nullValue|noValue|executeFormula|executeGroovy)/i },
  { key: 'rede', label: 'Rede / HTTP', icon: 'global', re: /(getHost|tryConnection|urlShorter|graphQL)/i },
];

@Component({
  selector: 'app-formula-lab',
  templateUrl: './formula-lab.component.html',
  styleUrls: ['./formula-lab.component.scss'],
  standalone: false,
})
export class FormulaLabComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('aceHost', { static: false }) aceHost!: ElementRef;
  private aceEditor: any;
  private aceReady = false;

  formula = '';
  popup = false;            // true quando o lab e aberto como dialog (via FormulaServices.openFormula)
  novo = true;
  tema: 'dark' | 'light' = 'dark';
  modo: 'single' | 'code' = 'single';
  listvariable: Variable[] = [];
  tipos = ['TEXTO', 'JSON', 'XML'];

  catalogo: FuncaoStudio[] = [];
  categorias: Categoria[] = [];
  filtro = '';
  selected: FuncaoStudio | null = null;

  resultado: any = null;
  resultadoTexto = '';
  tipoResultado = 'TEXTO';
  error: string | null = null;
  loading = false;
  tempoMs: number | null = null;
  copiado = false;
  traceLines: TraceItem[] = [];
  notaExemplo: string | null = null;
  private suppressLive = false;
  aviator = false;

  exemplosSingle: Exemplo[] = [
    { label: 'Validar CPF', formula: 'ifThenElse(regexTest({@CPF},"^[0-9]{11}$"),"valido","invalido")', variaveis: [{ key: '@CPF', value: '12345678901', type: 'TEXTO' }] },
    { label: 'So digitos', formula: 'regexReplace({@FONE},"[^0-9]","")', variaveis: [{ key: '@FONE', value: '+55 (11) 98765-4321', type: 'TEXTO' }] },
    { label: 'Data ISO -> BR', formula: 'regexReplace({@D},"([0-9]{4})-([0-9]{2})-([0-9]{2})","$3/$2/$1")', variaveis: [{ key: '@D', value: '2026-06-04', type: 'TEXTO' }] },
    { label: 'Mascarar cartao', formula: 'regexReplace({@CARD},"[0-9]{12}([0-9]{4})","************$1")', variaveis: [{ key: '@CARD', value: '4111111111111234', type: 'TEXTO' }] },
    { label: 'Buscar no JSON', formula: 'findJSON({@J},nome)', variaveis: [{ key: '@J', value: '{"nome":"Charles"}', type: 'JSON' }] },
  ];

  exemplosCodigo: Exemplo[] = [
    {
      label: 'Email -> dominio',
      modo: 'code',
      variaveis: [],
      formula:
`// Extrair dominio do e-mail e formatar
setVar(@EMAIL, "charles@temapi.com.br")
setVar(@DOMINIO, regexReplace({@EMAIL},".*@",""))
setVar(@UPPER, toUpperCase({@DOMINIO}))
concatenar("[", {@UPPER}, "]")`,
    },
    {
      label: 'Limpar + validar CPF',
      modo: 'code',
      variaveis: [],
      formula:
`// Remover mascara e validar CPF
setVar(@CPF_RAW, "123.456.789-01")
setVar(@CPF, regexReplace({@CPF_RAW},"[^0-9]",""))
ifThenElse(regexTest({@CPF},"^[0-9]{11}$"),concatenar("Valido: ",{@CPF}),"CPF invalido")`,
    },
    {
      label: 'Preco com desconto',
      modo: 'code',
      variaveis: [],
      formula:
`// Calcular preco final com desconto percentual
setVar(@PRECO, 150)
setVar(@PCT_DESC, 0.10)
setVar(@DESCONTO, multiply({@PRECO},{@PCT_DESC}))
setVar(@FINAL, subtract({@PRECO},{@DESCONTO}))
concatenar("R$ ", {@FINAL})`,
    },
    {
      label: 'JSON + saudacao',
      modo: 'code',
      variaveis: [],
      formula:
`// Ler JSON e montar mensagem personalizada
setVar(@NOME, findJSON({"nome":"Charles","nivel":"admin"},nome))
setVar(@NIVEL, toUpperCase(findJSON({"nome":"Charles","nivel":"admin"},nivel)))
concatenar("Ola ", {@NOME}, " [", {@NIVEL}, "]")`,
    },
    {
      label: 'Data atual formatada',
      modo: 'code',
      variaveis: [],
      formula:
`// Montar data atual no formato dd/MM/yyyy
setVar(@AGORA, getDate())
setVar(@DIA, datePart({@AGORA},"DAY"))
setVar(@MES, datePart({@AGORA},"MONTH"))
setVar(@ANO, datePart({@AGORA},"YEAR"))
concatenar({@DIA}, "/", {@MES}, "/", {@ANO})`,
    },
    {
      label: 'Precificacao (custo->margem->imposto)',
      modo: 'code',
      variaveis: [],
      formula:
`// Pipeline de precificacao encadeando calculos
setVar(@CUSTO, 80.00)
setVar(@MARGEM, multiply({@CUSTO}, 0.35))
setVar(@PRECO_BASE, add({@CUSTO}, {@MARGEM}))
setVar(@IMPOSTO, multiply({@PRECO_BASE}, 0.18))
setVar(@PRECO_FINAL, roundValue(add({@PRECO_BASE}, {@IMPOSTO}), 2))
concatenar("Custo ", {@CUSTO}, " -> Preco final R$ ", {@PRECO_FINAL})`,
    },
    {
      label: 'Validar + mascarar CPF (regex + condicao)',
      modo: 'code',
      variaveis: [],
      formula:
`// Limpa, valida por regex e mascara um CPF, com ramo condicional
setVar(@CPF_RAW, "529.982.247-25")
setVar(@CPF, regexReplace({@CPF_RAW}, "[^0-9]", ""))
setVar(@VALIDO, regexTest({@CPF}, "^[0-9]{11}$"))
setVar(@MASCARADO, formatString({@CPF}, "###.###.###-##"))
ifThenElse({@VALIDO}, concatenar("CPF valido: ", {@MASCARADO}), "CPF invalido")`,
    },
    {
      label: 'Ler JSON + De/Para de status',
      modo: 'code',
      variaveis: [],
      formula:
`// Extrai campos de um JSON (literal inline) e traduz o status
setVar(@NOME, findJSON({"pedido":{"cliente":{"nome":"Maria"},"status":"PAGO"}}, pedido.cliente.nome))
setVar(@STATUS, findJSON({"pedido":{"cliente":{"nome":"Maria"},"status":"PAGO"}}, pedido.status))
setVar(@MSG, dataSwitch({@STATUS}, "PAGO,Pagamento confirmado,PENDENTE,Aguardando", "Desconhecido"))
concatenar("Ola ", {@NOME}, "! ", {@MSG})`,
    },
  ];

  get exemplos(): Exemplo[] {
    return this.modo === 'code' ? this.exemplosCodigo : this.exemplosSingle;
  }

  private live$ = new Subject<void>();
  private sub?: Subscription;

  constructor(
    private http: HTTPService,
    private cat: FormulaCatalogService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<any>,
  ) {}

  ngOnInit(): void {
    // Modo dialog: na rota o Material injeta data={} (vazio) + dialogRef nao-nulo,
    // entao so e popup quando data tem conteudo real (mesmo criterio do studio).
    this.popup = !!(this.data && Object.keys(this.data).length > 0);
    if (this.data) {
      if (this.data.formula) this.formula = this.data.formula;
      if (Array.isArray(this.data.variables)) this.listvariable = [...this.data.variables];
    }
    try { const t = localStorage.getItem('fmlab-tema'); if (t === 'light' || t === 'dark') this.tema = t; } catch (e) {}
    try { const m = localStorage.getItem('fmlab-modo'); if (m === 'single' || m === 'code') this.modo = m; } catch (e) {}
    this.cat.load().then(c => { this.catalogo = c; this.recategorizar(); }).catch(e => console.error('catalogo formulas', e));
    this.sub = this.live$.pipe(debounceTime(450)).subscribe(() => {
      if (this.suppressLive) { this.suppressLive = false; return; }
      this.testar();
    });
  }

  // Modo dialog (chamado pelo rodape OK/Cancelar quando popup=true): devolve a
  // formula editada pro chamador (FormulaServices.openFormula le f['formula']).
  confirmar(): void { this.dialogRef?.close({ formula: this.formula, variables: this.listvariable }); }
  cancelar(): void { this.dialogRef?.close(); }

  private aceTheme(): string { return this.tema === 'dark' ? 'ace/theme/tomorrow_night' : 'ace/theme/github'; }

  toggleTema(): void {
    this.tema = this.tema === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem('fmlab-tema', this.tema); } catch (e) {}
    if (this.aceReady) this.aceEditor.setTheme(this.aceTheme());
  }

  toggleAviator(){
    this.aviator = !this.aviator;
    if( this.aviator ){
      this.modo = 'single';
      this.novo = false;
    }
  }

  toggleModo(): void {
    this.modo = this.modo === 'single' ? 'code' : 'single';
    try { localStorage.setItem('fmlab-modo', this.modo); } catch (e) {}
    this.traceLines = [];
    this.resultado = null; this.resultadoTexto = ''; this.error = null; this.tempoMs = null;
    if (this.aceReady) this.aceEditor.session.setMode(this.aceMode());
  }

  private aceMode(): string { return 'ace/mode/formula_lab'; }

  private registerAceMode(): void {
    (ace as any).define('ace/mode/formula_lab', [
      'require', 'exports', 'module',
      'ace/lib/oop', 'ace/mode/text', 'ace/mode/text_highlight_rules',
    ], (acerequire: any, exports: any) => {
      const oop = acerequire('ace/lib/oop');
      const TextMode = acerequire('ace/mode/text').Mode;
      const TextHighlightRules = acerequire('ace/mode/text_highlight_rules').TextHighlightRules;

      const FormulaLabRules = function(this: any) {
        this.$rules = { start: [
          { token: 'comment.line',       regex: /\/\/.*$/            },
          { token: 'string',             regex: /"(?:[^"\\]|\\.)*"/  },
          { token: 'string',             regex: /'(?:[^'\\]|\\.)*'/  },
          { token: 'variable.language',  regex: /\{@[A-Z0-9_]+\}/i  },
          { token: 'keyword.control',    regex: /\bsetVar\b(?=\s*\()/ },
          { token: 'support.function',   regex: /\b[a-zA-Z][a-zA-Z0-9_]*(?=\s*\()/ },
          { token: 'constant.numeric',   regex: /\b\d+(?:\.\d+)?\b/  },
          { token: 'text',               regex: /[^]/                 },
        ]};
      };
      oop.inherits(FormulaLabRules, TextHighlightRules);

      const Mode = function(this: any) {
        this.HighlightRules = FormulaLabRules;
        this.$id = 'ace/mode/formula_lab';
        (this as any).lineCommentStart = '//';
      };
      oop.inherits(Mode, TextMode);
      exports.Mode = Mode;
    });
  }

  ngAfterViewInit(): void {
    if (!this.aceHost) return;
    this.registerAceMode();
    this.aceEditor = ace.edit(this.aceHost.nativeElement);
    this.aceEditor.setTheme(this.aceTheme());
    this.aceEditor.session.setMode(this.aceMode());
    this.aceEditor.session.setUseWorker(false);
    this.aceEditor.session.setUseWrapMode(true);
    this.aceEditor.setOptions({
      fontSize: '14px', showPrintMargin: false, highlightActiveLine: true, showGutter: true,
      minLines: 8, maxLines: 24, enableBasicAutocompletion: true, enableLiveAutocompletion: true,
    });
    try {
      const langTools = (ace as any).require('ace/ext/language_tools');
      const self = this;
      langTools.setCompleters([{
        getCompletions: (_e: any, _s: any, _p: any, _pref: string, cb: Function) => {
          const fns = self.catalogo.map(f => ({ caption: f.name, value: (f.sintaxe?.expression || f.name) + '(', meta: 'funcao', score: 1000 }));
          const vars = (self.listvariable || []).map(v => ({ caption: v.key, value: v.key, meta: 'variavel', score: 900 }));
          cb(null, [...fns, ...vars]);
        },
      }]);
    } catch (e) {}
    this.aceReady = true;
    // popup: carrega a formula recebida no editor (antes de ligar o listener de change)
    if (this.formula) { this.suppressLive = true; this.aceEditor.session.setValue(this.formula, -1); }
    this.aceEditor.session.on('change', () => {
      this.formula = this.aceEditor.getValue();
      this.live$.next();
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); try { this.aceEditor?.destroy?.(); } catch (e) {} }

  private categoriaDe(n: string): string { return (CATS.find(c => c.re.test(n)) || { key: 'outros' }).key; }

  recategorizar(): void {
    const t = (this.filtro || '').toLowerCase();
    const fonte = !t ? this.catalogo : this.catalogo.filter(f => f.name.toLowerCase().includes(t) || (f.description || '').toLowerCase().includes(t));
    const mapa: { [k: string]: FuncaoStudio[] } = {};
    for (const f of fonte) (mapa[this.categoriaDe(f.name)] ??= []).push(f);
    const ordem = [...CATS, { key: 'outros', label: 'Outros', icon: 'appstore', re: /.*/ }];
    this.categorias = ordem.filter(c => mapa[c.key]?.length).map(c => ({ key: c.key, label: c.label, icon: c.icon, items: mapa[c.key], aberta: !!t }));
  }

  filtrar(t: string): void { this.filtro = t; this.recategorizar(); }
  toggleCat(c: Categoria): void { c.aberta = !c.aberta; }
  selecionar(f: FuncaoStudio): void { this.selected = f; }

  inserir(f: FuncaoStudio): void {
    const nome = f.sintaxe?.expression || f.name;
    if (this.aceReady) {
      this.aceEditor.insert(nome + '()');
      const pos = this.aceEditor.getCursorPosition();
      this.aceEditor.moveCursorTo(pos.row, pos.column - 1);
      this.aceEditor.focus();
    } else { this.formula = (this.formula || '') + nome + '()'; }
  }

  testarExemplo(f: FuncaoStudio): void {
    if (!f.exemplo) return;
    this.suppressLive = true; // evita que o setValue do Ace dispare um run pelo live$
    this.listvariable = (f.variaveis || []).map(v => ({ ...v }));
    this.formula = f.exemplo;
    if (this.aceReady) this.aceEditor.session.setValue(f.exemplo, -1);
    if (f.runnable === false) {
      this.resultado = null; this.resultadoTexto = ''; this.error = null; this.tempoMs = null; this.traceLines = [];
      this.notaExemplo = f.combina || 'Este exemplo precisa de contexto de fluxo, credencial ou sistema externo e nao executa no laboratorio isolado. A formula foi carregada para voce ver a sintaxe.';
      return;
    }
    this.notaExemplo = null;
    this.testar();
  }

  carregarExemplo(ex: Exemplo): void {
    this.listvariable = ex.variaveis.map(v => ({ ...v }));
    this.formula = ex.formula;
    if (this.aceReady) this.aceEditor.session.setValue(ex.formula, -1);
    if (ex.modo && ex.modo !== this.modo) {
      this.modo = ex.modo;
      try { localStorage.setItem('fmlab-modo', this.modo); } catch (e) {}
      if (this.aceReady) this.aceEditor.session.setMode(this.aceMode());
    }
    this.traceLines = [];
    this.testar();
  }

  addVariavel(): void { this.listvariable = [...this.listvariable, { key: '', value: '', type: 'TEXTO' }]; }
  removeVariavel(i: number): void { this.listvariable.splice(i, 1); this.listvariable = [...this.listvariable]; this.live$.next(); }
  variavelMudou(): void { this.live$.next(); }

  buildTestPayload(formula: string, variables: Variable[], novo: boolean): any {
    // O toggle "novo" do Studio seleciona o MOTOR NOVO isolado (motorNovo).
    // "antigo" = motor de producao com o parametro novo interno (novo:true), como hoje.
    const ob: any = { expressao: formula, novo: true, motorNovo: novo, values: variables };
    for (const d of variables || []) {
      if (d.key === '@CODECOMPANY') ob.codecompany = d.value;
      if (d.key === '@CODEMKTP') ob.codemktp = d.value;
      if (d.key === '@CODECONTRACT') ob.codecontract = d.value;
    }
    return ob;
  }

  private parseSetVar(line: string): { varName: string; formula: string } | null {
    const m = line.match(/^setVar\(\s*(@[A-Z0-9_]+)\s*,\s*([\s\S]+)\)\s*$/i);
    if (!m) return null;
    return { varName: m[1], formula: m[2].trim() };
  }

  private executeFormulaAsync(formula: string, vars: Variable[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const ob = this.buildTestPayload(formula, vars, this.novo);
      ob.sandbox = true;
      ob.aviator = this.aviator; 
      this.http.post(ob, 'FormulaServices/test', true, true, false).subscribe({
        next: (x: any) => resolve(x['resultado'] ?? null),
        error: (e: any) => {
          const msg = e?.error?.message || e?.error?.messageRequest || e?.message || 'Erro';
          reject(new Error(msg));
        },
      });
    });
  }

  testar(): void {
    if (this.modo === 'code') { this.testarCodigo(); return; }
    if (!this.formula || !this.formula.trim()) {
      this.resultado = null; this.resultadoTexto = ''; this.error = null; this.tempoMs = null; return;
    }
    this.notaExemplo = null;
    this.error = null; this.loading = true;
    const ini = performance.now();
    const ob = this.buildTestPayload(this.formula, this.listvariable, this.novo);
    ob.sandbox = true;
    ob.aviator = this.aviator;
    this.http.post(ob, 'FormulaServices/test', true, true, false).subscribe({
      next: (x: any) => { this.tempoMs = Math.round(performance.now() - ini); this.setResultado(x['resultado'] ?? null); this.loading = false; },
      error: (e: any) => {
        this.tempoMs = Math.round(performance.now() - ini);
        if (e?.status === 429) this.error = (e?.error?.message) || 'Limite de testes atingido. Aguarde um minuto e tente de novo.';
        else this.error = e?.error?.message || e?.message || 'Erro ao testar';
        this.resultado = null; this.resultadoTexto = ''; this.loading = false;
      },
    });
  }

  // Modo codigo: 1 unica chamada ao /testScript (server-side processa linha-a-linha e
  // devolve o trace). Performatico (nao faz N requests) e identico ao que roda em job/fluxo
  // via a funcao executeScript/runScript no backend.
  testarCodigo(): void {
    const script = this.formula || '';
    const linhasValidas = script.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('//'));
    if (!linhasValidas.length) {
      this.resultado = null; this.resultadoTexto = ''; this.error = null; this.tempoMs = null; this.traceLines = []; return;
    }
    this.loading = true; this.error = null; this.traceLines = []; this.notaExemplo = null;
    const start = performance.now();
    const ob: any = { expressao: script, novo: this.novo, sandbox: true, values: this.listvariable, aviator: this.aviator };
    for (const d of this.listvariable || []) {
      if (d.key === '@CODECOMPANY') ob.codecompany = d.value;
      if (d.key === '@CODEMKTP') ob.codemktp = d.value;
      if (d.key === '@CODECONTRACT') ob.codecontract = d.value;
    }
    this.http.post(ob, 'FormulaServices/testScript', true, true, false).subscribe({
      next: (x: any) => {
        this.tempoMs = Math.round(performance.now() - start);
        const trace = (x['trace'] || []) as any[];
        this.traceLines = trace.map(t => ({ lineNo: t.linha, formula: t.formula, result: t.resultado, varSet: t.varSet, error: t.erro, ms: 0 }));
        if (x['erro']) { this.error = x['erro']; this.resultado = null; this.resultadoTexto = ''; }
        else { this.setResultado(x['resultado'] ?? null); }
        this.loading = false;
      },
      error: (e: any) => {
        this.tempoMs = Math.round(performance.now() - start);
        if (e?.status === 429) this.error = (e?.error?.message) || 'Limite de testes atingido. Aguarde um minuto e tente de novo.';
        else this.error = e?.error?.message || e?.error?.messageRequest || e?.message || 'Erro ao testar o script';
        this.resultado = null; this.resultadoTexto = ''; this.loading = false;
      },
    });
  }

  private setResultado(res: any): void {
    this.resultado = res;
    const txt = res == null ? '' : (typeof res === 'string' ? res : JSON.stringify(res));
    const t = (txt || '').trim();
    this.tipoResultado = 'TEXTO';
    if (t.startsWith('{') || t.startsWith('[')) { try { this.resultadoTexto = JSON.stringify(JSON.parse(t), null, 2); this.tipoResultado = 'JSON'; return; } catch (e) {} }
    if (t.startsWith('<')) this.tipoResultado = 'XML';
    this.resultadoTexto = txt;
  }

  traceResultadoTexto(r: any): string {
    if (r == null) return '';
    const s = typeof r === 'string' ? r : JSON.stringify(r);
    return s.length > 120 ? s.slice(0, 120) + '...' : s;
  }

  copiar(): void { try { navigator.clipboard?.writeText(this.resultadoTexto); this.copiado = true; setTimeout(() => (this.copiado = false), 1500); } catch (e) {} }
  limpar(): void {
    this.formula = '';
    if (this.aceReady) this.aceEditor.session.setValue('', -1);
    this.resultado = null; this.resultadoTexto = ''; this.error = null; this.tempoMs = null; this.traceLines = [];
  }
}
